import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Button,
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';

interface Customer {
  id: string;
  displayName: string;
  companyName?: string;
  givenName?: string;
  familyName?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  balance?: number;
  currencyCode?: string;
  isActive?: boolean;
  associatedAt?: string;
}

interface VendorData {
  id: string;
  displayName: string;
  associatedCustomers: Customer[];
}

export const VendorCustomersPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [vendorData, setVendorData] = useState<VendorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendorData = async () => {
    if (!vendorId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}`,
        {
          timeout: 30000 // 30 seconds timeout
        }
      );

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this vendor.');
        }
        if (response?.status === 404) {
          throw new Error('Vendor not found');
        }
        throw new Error('Failed to fetch vendor details');
      }

      const data = await response.json();
      setVendorData({
        id: data.data.id,
        displayName: data.data.displayName,
        associatedCustomers: data.data.associatedCustomers || []
      });
    } catch (err: any) {
      console.error('Error fetching vendor data:', err);
      setError(err.message || 'Failed to fetch vendor data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorData();
  }, [vendorId]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/hub/accounting/vendors')}
        >
          Back to Vendors
        </Button>
      </Container>
    );
  }

  if (!vendorData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No vendor data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
        >
          {vendorData.displayName}
        </Link>
        <Typography color="text.primary">Associated Customers</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <BusinessIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                Associated Customers
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {vendorData.displayName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}/customer/new`)}
            >
              Add New Customer
            </Button>
            <IconButton onClick={fetchVendorData} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Customers Table */}
      <Paper sx={{ p: 3 }}>
        {vendorData.associatedCustomers && vendorData.associatedCustomers.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Primary Phone</TableCell>
                  <TableCell>Primary Email</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorData.associatedCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => navigate(`/admin/hub/accounting/customers/${customer.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {customer.displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>{customer.companyName || '-'}</TableCell>
                    <TableCell>{customer.primaryPhone || '-'}</TableCell>
                    <TableCell>{customer.primaryEmail || '-'}</TableCell>
                    <TableCell align="right">
                      {customer.balance !== undefined && customer.balance !== null ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: customer.balance === 0 ? 'green' : 'error.main'
                          }}
                        >
                          {formatCurrency(customer.balance, customer.currencyCode)}
                        </Typography>
                      ) : (
                        <Typography variant="body2">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No customers are associated with this vendor. To associate customers, use the vendor-customer relationship table.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};